import { ContainerPadding, Spinner, Text } from '@volkswagen-onehub/components-core';
import { Magnifier } from '@volkswagen-onehub/icons-core';
import React, { useEffect, useMemo, useState } from 'react';
import { TextInputExtended } from '../../../components/inputs-group/inputs/core/text-input-extended';
import { DealerOptionCard } from '../../../components/inputs-group/inputs/dealer-option-card';
import { CardsGroup } from '../../../components/screen/cards-group';
import { ErrorSection } from '../../../components/screen/error';
import { InputsGroup } from '../../../components/screen/inputs-group';
import { FIELD_NAME, FORM_MESSAGE, FORM_SUBMIT_STATUS } from '../../../constants/form-constants';
import { formService } from '../../../form-service';
import { DealersService } from '../../../service/api/get-dealers';
import { NdpsDealer } from '../../../types/dealer-types';

// Número mínimo de caracteres para iniciar la búsqueda.
const MIN_SEARCH_LENGTH = 3;

export function CallCenterDealersRadio() {
	const [searchValue, setSearchValue] = useState('');
	const [dealers, setDealers] = useState<NdpsDealer[]>([]);
	const [selectedDealer, setSelectedDealer] = useState<NdpsDealer | null>(null);
	const [isSearching, setIsSearching] = useState(false);
	const [showDealers, setShowDealers] = useState(false);
	const [hasError, setHasError] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const dealersService = useMemo(() => new DealersService(), []);

	// Iniciamos el campo requerido y creamos la suscripcion de eventos
	useEffect(() => {
		formService.setFieldRequired(FIELD_NAME.DEALER, true);
		const unsubscribeField = formService.subscribeToField(FIELD_NAME.DEALER, {
			onChange: (value) => {
				const invalid = !value && formService.formSubmitStatus() === FORM_SUBMIT_STATUS.INVALID;
				setHasError(invalid);
				if (formService.formSubmitStatus() === FORM_SUBMIT_STATUS.INVALID) {
					formService.setFieldError(FIELD_NAME.DEALER, !value);
				}
			},
			onError: (error) => setHasError(error),
		});
		const unsubscribeStatus = formService.subscribeToFormSubmitStatus((status) => {
			if (status === FORM_SUBMIT_STATUS.INVALID) {
				const value = formService.getFieldValue(FIELD_NAME.DEALER);
				setHasError(!value);
				formService.setFieldError(FIELD_NAME.DEALER, !value);
			}
		});
		return () => {
			unsubscribeField();
			unsubscribeStatus();
		};
	}, []);

	// Ejecuta la búsqueda de dealers con debounce cuando cambia el valor de búsqueda
	useEffect(() => {
		const value = searchValue.trim();
		if (value.length < MIN_SEARCH_LENGTH) {
			setDealers([]);
			setShowDealers(false);
			setIsSearching(false);
			return;
		}

		setError(null);
		setIsSearching(true);
		const timeoutId = setTimeout(async () => {
			try {
				const results = await dealersService.searchDealers(value);
				setDealers(results);
				setShowDealers(true);
				setIsSearching(false);
			} catch (e) {
				const msg = 'Error obteniendo instalaciones.';
				setError(msg);
				console.error(msg, e);
				setDealers([]);
				setShowDealers(false);
				setIsSearching(false);
			}
		}, 500);

		return () => clearTimeout(timeoutId);
	}, [searchValue, dealersService]);

	// Manejo del input de búsqueda
	const handleChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
		setError(null);
		const value = (e.target as HTMLInputElement).value;
		setSearchValue(value);
		setSelectedDealer(null);
		formService.setFieldValue(FIELD_NAME.DEALER, '');
	};

	const handleDealerSelect = (dealer: NdpsDealer) => {
		setSelectedDealer(dealer);
		formService.setFieldValue(FIELD_NAME.DEALER, dealer ?? '');
	};

	const filteredDealers = selectedDealer ? dealers.filter((d) => `${d.Id}` == selectedDealer.Id) : dealers;

	return (
		<>
			<InputsGroup paddingBottom={ContainerPadding.static500}>
				<TextInputExtended
					name={FIELD_NAME.SEARCHED_DEALER}
					value={searchValue}
					onChange={handleChange}
					label="Instalación"
					required
					icon={<Magnifier variant="default" aria-hidden />}
					autoComplete="off"
					hasError={hasError}
					errorMessage="Debes buscar y seleccionar una opción"
				/>
			</InputsGroup>

			{isSearching ? (
				<Spinner variant="large" />
			) : (
				showDealers &&
				(filteredDealers.length > 0 ? (
					<CardsGroup paddingBottom={ContainerPadding.static300}>
						{filteredDealers.map((dealer) => {
							const dealerId = `${dealer.Id}`;
							return (
								<DealerOptionCard
									key={dealerId}
									dealer={dealer}
									isActive={selectedDealer?.Id == dealerId}
									onSelect={() => handleDealerSelect(dealer)}
									hasError={hasError}
									showDetails={true}
									highlight={searchValue}
								/>
							);
						})}
					</CardsGroup>
				) : (
					<Text>No hay resultados</Text>
				))
			)}
			{error ? (
				<ErrorSection align="left" log={error}>
					{FORM_MESSAGE.GENERIC_ERROR}
				</ErrorSection>
			) : undefined}
		</>
	);
}
